import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@swot/base-components";
import { SwotFieldRenderer, SwotFormErrorSummary, SwotFormShell, SwotSubmitButton, type SwotFieldDefinition } from "@swot-shared/forms/index.js";
import { CmtsFieldErrors } from "./CmtsFieldErrors.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type CmtsFormField = {
  name: string;
  label: string;
  type?: "text" | "textarea" | "select" | "date" | "datetime-local" | "number" | undefined;
  required?: boolean | undefined;
  options?: readonly { readonly value: string; readonly label: string }[] | undefined;
  placeholder?: string | undefined;
  help?: string | undefined;
  minLength?: number | undefined;
  maxLength?: number | undefined;
  readOnly?:boolean;
  disabled?:boolean;
};

export type CmtsEnterpriseFormProps = {
  readonly fields: readonly CmtsFormField[];
  readonly initialValues?: Record<string, unknown> | undefined;
  readonly submitLabel?: string | undefined;
  readonly cancelLabel?: string | undefined;
  readonly busy?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly children?: ReactNode | undefined;
  readonly onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
  readonly onCancel?: (() => void) | undefined;
  readonly onValuesChange?: ((payload: Record<string, unknown>) => void) | undefined;
};


function toSwotFieldDefinition(field: CmtsFormField, disabled: boolean): SwotFieldDefinition {
  const type: SwotFieldDefinition["type"] = field.type === "datetime-local" ? "datetime" : field.type ?? "text";
  const definition: SwotFieldDefinition = {
    name: field.name,
    label: field.label,
    type,
    disabled,
    required: field.required,
    placeholder: field.placeholder,
    description: field.help,
    options: field.options,
    maxLength: field.maxLength,
  };
  return definition;
}

function valueAsString(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

function buildInitialFormValues(fields: readonly CmtsFormField[], initialValues?: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const field of fields) result[field.name] = valueAsString(initialValues?.[field.name]);
  return result;
}

function stableInitialValuesSignature(fields: readonly CmtsFormField[], initialValues?: Record<string, unknown>): string {
  return fields.map((field) => `${field.name}:${valueAsString(initialValues?.[field.name])}`).join("|");
}

export function CmtsEnterpriseForm(props: CmtsEnterpriseFormProps) {
  const initialValuesSignature = useMemo(
    () => stableInitialValuesSignature(props.fields, props.initialValues),
    [props.fields, props.initialValues]
  );

  const initial = useMemo(
    () => buildInitialFormValues(props.fields, props.initialValues),
    [props.fields, initialValuesSignature]
  );

  const [values, setValues] = useState<Record<string, string>>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const isSubmitting = Boolean(props.busy || localSubmitting);

  useEffect(() => {
    setValues(initial);
    setFieldErrors({});
    setSubmitError(null);
    setDirty(false);
  }, [initialValuesSignature, initial]);

  const updateValue = (fieldName: string, value: unknown) => {
    const nextValue = valueAsString(value);

    setValues((current) => {
      const next = { ...current, [fieldName]: nextValue };
      props.onValuesChange?.({ ...props.initialValues, ...next });
      return next;
    });

    setDirty(true);
    setFieldErrors((current) => ({ ...current, [fieldName]: [] }));
  };

  const validate = () => {
    const next: Record<string, string[]> = {};

    for (const field of props.fields) {
      const value = String(values[field.name] ?? "").trim();

      if (field.required && !value) {
        next[field.name] = [`${field.label} is required.`];
      }

      if (field.minLength && value && value.length < field.minLength) {
        next[field.name] = [`${field.label} must be at least ${field.minLength} characters.`];
      }

      if (field.maxLength && value && value.length > field.maxLength) {
        next[field.name] = [`${field.label} must be no more than ${field.maxLength} characters.`];
      }
    }

    setFieldErrors(next);
    return Object.values(next).every((messages) => messages.length === 0);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (isSubmitting) return;

    setSubmitError(null);

    if (!validate()) return;

    const payload: Record<string, unknown> = { ...props.initialValues };

    for (const field of props.fields) {
      payload[field.name] = values[field.name] ?? "";
    }

    setLocalSubmitting(true);

    try {
      await props.onSubmit(payload);
      setDirty(false);
      setFieldErrors({});
    } catch (error: unknown) {
      setSubmitError(normalizeUserSafeApiError(error).userMessage || "Save failed. Review the form values and retry.");
    } finally {
      setLocalSubmitting(false);
    }
  };

  const validationMessages = [
    ...Object.values(fieldErrors).flat(),
    ...(submitError ? [submitError] : []),
    ...(props.error ? [props.error] : []),
  ];

  return (
    <SwotFormShell
      className="cmts-enterprise-form"
      onSubmit={submit}
      dirty={dirty}
      disabled={isSubmitting}
      errorSummary={
        <SwotFormErrorSummary
          title="Review the highlighted fields"
          message={props.error ?? submitError ?? undefined}
          fieldErrors={fieldErrors}
        />
      }
      actions={
        <div className="cmts-enterprise-form__actions">
          {props.onCancel ? (
            <Button
              label={props.cancelLabel ?? "Cancel"}
              variant="secondary"
              className="cmts-button cmts-button--secondary"
              onClick={props.onCancel}
              disabled={isSubmitting}
            />
          ) : null}

          <SwotSubmitButton pending={isSubmitting} disabled={isSubmitting}>
            {props.submitLabel ?? "Save"}
          </SwotSubmitButton>
        </div>
      }
    >
      {validationMessages.length > 0 ? (
        <ul className="cmts-enterprise-form__validation-summary">
          {validationMessages.map((message, index) => (
            <li key={`${message}-${index}`}>{message}</li>
          ))}
        </ul>
      ) : null}

      <div className="cmts-enterprise-form__grid">
        {props.fields.map((field) => {
          const value = values[field.name] ?? "";
          const fieldMessages = fieldErrors[field.name] ?? [];

          return (
            <div
              key={field.name}
              className={
                field.type === "textarea"
                  ? "cmts-enterprise-form__field cmts-enterprise-form__field--wide"
                  : "cmts-enterprise-form__field"
              }
            >
              <SwotFieldRenderer
                field={toSwotFieldDefinition(field, isSubmitting)}
                value={value}
                error={fieldMessages.length ? <CmtsFieldErrors fieldName={field.name} errors={fieldErrors} /> : undefined}
                onChange={(fieldName, nextValue) => updateValue(fieldName, nextValue)}
              />
            </div>
          );
        })}
      </div>

      {props.children}
    </SwotFormShell>
  );
}